/**
 * ValidationUploadPage.tsx
 *
 * The Validation page — a three-step workflow for the monthly consumption
 * figure GRATO submits to IHS, and what comes back once IHS has reviewed
 * it:
 *
 *   1. System: Submitted Consumption — computed directly from field-visit
 *      and Tom Card data already in the platform (Delivered This Cycle
 *      minus Closing Stock, per site). This used to be a number GRATO
 *      computed by hand in Excel every cycle; the system now does the
 *      same arithmetic from data it already has, so GRATO can sanity-check
 *      it against this cycle's Tom Card purchases before sending.
 *   2. Negotiation (manual, off-platform) — IHS reviews and negotiates
 *      the figure with GRATO outside the platform. Nothing to compute or
 *      upload here; this step exists only to show where the process
 *      currently is.
 *   3. Upload Agreed File & Reports — once a figure is agreed, GRATO
 *      uploads the Validation Template (Main + Validation sheets) the
 *      same way it always has. This is the original upload workflow this
 *      page used to be, unchanged — see validationImportService.js on the
 *      backend for the full parsing contract — just moved to its place in
 *      the larger process instead of being the whole page.
 *
 * Visual conventions match the rest of the ported diesel app (same color
 * tokens, same KpiCard/Spinner/ErrorBox/Btn usage, same card/grid layout)
 * rather than the dark theme of the redesign mockup this was ported from —
 * consistent with every other page in this port.
 */
import React, { useState } from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { validationApi, reportsApi } from '../../api/dieselApi';
import {
  useCycles, useFileUpload, useValidationUploads, useValidationStats,
  useSubmittedConsumption,
} from '../../hooks/useDiesel';
import {
  PageHeader, CycleSelector, UploadZone, KpiCard, Card, SectionLabel, Tag,
  fmtNum, fmtDate, Spinner, ErrorBox, EmptyState, Btn, StatusPill,
} from '../../components/diesel/DieselComponents';
import { neutral, ink, status as statusTokens, urgency as urgencyTokens, font } from '../../styles/dieselTokens';

type Step = 1 | 2 | 3;

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'System: Submitted Consumption' },
  { n: 2, label: 'Negotiation (manual, off-platform)' },
  { n: 3, label: 'Upload Agreed File & Reports' },
];

function StepIndicator({ step, onChange }: { step: Step; onChange: (s: Step) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24, flexWrap: 'wrap' }}>
      {STEPS.map((s, i) => {
        const active = s.n === step;
        const done = s.n < step;
        return (
          <React.Fragment key={s.n}>
            <button
              onClick={() => onChange(s.n)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '6px 10px', borderRadius: 8,
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: active ? ink : done ? urgencyTokens.ok.bg : neutral[100],
                color: active ? '#fff' : done ? urgencyTokens.ok.fg : neutral[400],
                border: active ? 'none' : `1px solid ${done ? urgencyTokens.ok.fg : neutral[200]}`,
              }}>
                {s.n}
              </span>
              <span style={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? ink : neutral[500] }}>
                {s.label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <span style={{ width: 24, height: 1, background: neutral[200], margin: '0 2px' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ValidationUploadPage() {
  const { isNarrow } = useBreakpoint();
  const [step, setStep] = useState<Step>(1);

  // ── Shared cycle selector (step 1's computation is cycle-scoped) ──
  const { cycles, selectedKey, setSelectedKey } = useCycles();

  // ── Step 1: computed submitted consumption ──
  const { data: submitted, loading: submittedLoading, error: submittedError, refetch: refetchSubmitted } =
    useSubmittedConsumption(selectedKey);
  // useFetch already unwraps one level (hook data = the backend's own
  // "data" field), but some pages' hooks end up exposing the full
  // {success, data} envelope instead depending on how the fetcher is
  // typed — same ambiguity ReconciliationPage's `(data as any)?.data || data`
  // guards against. Handle both shapes rather than assuming one.
  const submittedData = (submitted as any)?.data || submitted || null;
  const summary = submittedData?.summary;
  const siteRows = submittedData?.sites || [];

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  async function handleExport() {
    if (!selectedKey) return;
    setExporting(true);
    setExportError(null);
    try {
      await reportsApi.downloadFile(
        reportsApi.submittedConsumptionPath(selectedKey),
        `Submitted_Consumption_${selectedKey}.xlsx`
      );
    } catch (e: any) {
      setExportError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  // ── Step 3: original upload workflow, unchanged ──
  const { uploading, result, error, upload, reset } = useFileUpload();
  const { data: uploads, loading: uploadsLoading, refetch: refetchUploads } = useValidationUploads();
  const [statsCycle, setStatsCycle] = useState<string>('');
  const { data: stats, loading: statsLoading, refetch: refetchStats } = useValidationStats(statsCycle);

  const importResult = (result as any)?.data;
  const uploadList = (uploads as any) || [];
  const statsData = (stats as any)?.data || stats || null;

  async function handleFile(file: File) {
    reset();
    const res = await upload(() => validationApi.upload(file));
    refetchUploads();
    const cyclesTouched = (res as any)?.data?.validation?.cycles_touched || [];
    if (cyclesTouched.length > 0) {
      setStatsCycle(cyclesTouched[0]);
    }
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto', fontFamily: font.ui }}>
      <PageHeader
        title="Validation"
        subtitle="The system calculates what GRATO submits, and checks what comes back from the client — it doesn't run the negotiation itself. That stays manual, between GRATO and IHS, exactly as today."
        actions={step === 1 ? <CycleSelector cycles={cycles} value={selectedKey} onChange={setSelectedKey} /> : undefined}
      />

      <StepIndicator step={step} onChange={setStep} />

      {/* ───────────────────────── Step 1 ───────────────────────── */}
      {step === 1 && (
        <>
          <p style={{ fontSize: 13, color: neutral[500], lineHeight: 1.6, maxWidth: 900, marginBottom: 20 }}>
            This is the number GRATO has been computing by hand in Excel every cycle: fuel delivered to each
            site this cycle (from visits) minus the stock still on site at cycle end. The system now
            calculates it directly from visit, cycle-end stock, and Tom Card data already in the platform —
            ready to copy into the manual submission to IHS.
          </p>

          {!selectedKey ? (
            <EmptyState icon="📭" title="Pick a cycle" desc="Select a billing cycle above to see its submitted consumption." />
          ) : submittedLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
          ) : submittedError ? (
            <ErrorBox message={submittedError} onRetry={refetchSubmitted} />
          ) : !summary ? (
            <EmptyState icon="📭" title="Nothing to show yet" desc="No field visit or stock data found for this cycle." />
          ) : (
            <>
              {!summary.has_stock_snapshot && summary.sites_covered > 0 && (
                <div style={{ background: statusTokens.warning.bg, border: `1px solid ${statusTokens.warning.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: statusTokens.warning.text, lineHeight: 1.6 }}>
                  This cycle hasn't been closed yet, so there's no cycle-end stock snapshot for it. Closing
                  stock below is each site's last field visit reading instead — close the cycle (or capture a
                  snapshot from Site &amp; Cluster Stock) for the real end-of-cycle figure.
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
                <KpiCard
                  label="Submitted Consumption"
                  value={`${fmtNum(summary.submitted_consumption_l)} L`}
                  sub={`Delivered − closing stock, ${siteRows.length} site(s)`}
                  icon="⛽"
                />
                <KpiCard
                  label="Purchases This Cycle (Tom Card)"
                  value={`${fmtNum(summary.purchases_l)} L`}
                  sub={`${fmtNum(summary.purchase_transactions)} transaction(s) — see Tom Card for detail`}
                  icon="💳"
                />
                <KpiCard
                  label="Sites Covered"
                  value={summary.sites_covered}
                  icon="📍"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 24 }}>
                <KpiCard
                  label="Visits With a Closing-Stock Reading"
                  value={`${summary.visits_with_closing_stock} / ${summary.sites_covered}`}
                  sub={summary.sites_missing_closing_stock > 0 ? `${summary.sites_missing_closing_stock} site(s) missing a last-day reading` : 'All sites have a reading'}
                  tone={summary.sites_missing_closing_stock > 0 ? 'warn' : 'ok'}
                  icon="📋"
                />
                <KpiCard
                  label="Sites With a Run-Hours Reading"
                  value={`${summary.sites_with_run_hours} / ${summary.sites_covered}`}
                  sub={summary.sites_missing_run_hours > 0 ? `${summary.sites_missing_run_hours} site(s): meter fault or no reading` : 'All sites have a reading'}
                  tone={summary.sites_missing_run_hours > 0 ? 'warn' : 'ok'}
                  icon="⏱️"
                />
              </div>

              <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                {siteRows.length === 0 ? (
                  <div style={{ padding: 24 }}>
                    <EmptyState icon="📭" title="No field visits this cycle" desc="Submitted consumption needs at least one field visit per site for this cycle." />
                  </div>
                ) : (
                  <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead style={{ position: 'sticky', top: 0, background: neutral[50], zIndex: 1 }}>
                        <tr>
                          {['Site', 'Cluster', 'Delivered This Cycle', 'Last Visit Date', 'Closing Stock', 'Run Hours (SBC-reported)', 'Submitted Consumption'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: neutral[500], borderBottom: `1px solid ${neutral[200]}`, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {siteRows.map((s: any) => (
                          <tr key={s.site_id} style={{ borderBottom: `1px solid ${neutral[100]}` }}>
                            <td style={{ padding: '7px 12px', fontWeight: 600 }}>{s.site_id}<div style={{ fontWeight: 400, fontSize: 11, color: neutral[400] }}>{s.site_name}</div></td>
                            <td style={{ padding: '7px 12px', color: neutral[500] }}>{s.cluster || '—'}</td>
                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(s.delivered_l)} L</td>
                            <td style={{ padding: '7px 12px', color: neutral[500] }}>{fmtDate(s.last_visit_date)}</td>
                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {s.missing_closing_stock ? (
                                <Tag variant="warning">No reading</Tag>
                              ) : (
                                <>
                                  {fmtNum(s.closing_stock_l)} L
                                  {s.closing_stock_source === 'last_visit' && (
                                    <div style={{ fontWeight: 400, fontSize: 10, color: urgencyTokens.high.fg }}>last visit, not cycle-end</div>
                                  )}
                                </>
                              )}
                            </td>
                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {s.missing_run_hours ? <Tag variant="warning">Meter fault / no reading</Tag> : `${fmtNum(s.run_hours_l)} h`}
                            </td>
                            <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(s.submitted_consumption_l)} L</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <p style={{ fontSize: 11, color: neutral[400], lineHeight: 1.6, maxWidth: 900, marginBottom: 16 }}>
                Run hours travel with the submission as supporting evidence for the liters claimed — they
                don't feed the Delivered − Closing-stock arithmetic, but a site with no plausible run hours
                for that much fuel is exactly what IHS pushes back on first.
              </p>

              {exportError && <div style={{ marginBottom: 12 }}><ErrorBox message={exportError} onRetry={handleExport} /></div>}
              <Btn onClick={handleExport} loading={exporting}>
                ⬇ Export Submitted Consumption (for manual submission to IHS)
              </Btn>
            </>
          )}
        </>
      )}

      {/* ───────────────────────── Step 2 ───────────────────────── */}
      {step === 2 && (
        <Card style={{ maxWidth: 720 }}>
          <SectionLabel>Negotiation (manual, off-platform)</SectionLabel>
          <p style={{ fontSize: 13, color: neutral[700], lineHeight: 1.7, margin: '0 0 12px' }}>
            Once the Submitted Consumption figure is exported, GRATO sends it to IHS and the two sides
            negotiate the final agreed liters directly — by email, over calls, however that conversation
            normally happens. The platform doesn't run or track that exchange; it stays exactly as manual
            as it is today.
          </p>
          <p style={{ fontSize: 13, color: neutral[700], lineHeight: 1.7, margin: 0 }}>
            When GRATO and IHS land on a final figure, move to <strong>Step 3</strong> and upload the
            Validation Template reflecting what was agreed — the same file this page has always taken.
          </p>
          <div style={{ marginTop: 16 }}>
            <Btn variant="secondary" onClick={() => setStep(3)}>Continue to Step 3 →</Btn>
          </div>
        </Card>
      )}

      {/* ───────────────────────── Step 3 (original upload workflow) ───────────────────────── */}
      {step === 3 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '420px 1fr', gap: 24, marginBottom: 28 }}>
            {/* ── Upload panel ── */}
            <div>
              <UploadZone
                accept=".xlsx,.xls"
                label="Drop the agreed Validation Template here or click to browse"
                subLabel="Expects sheets named 'Main' and 'Validation'"
                onFile={handleFile}
                uploading={uploading}
              />

              <div style={{ background: statusTokens.info.bg, border: `1px solid ${statusTokens.info.border}`, borderRadius: 8, padding: '10px 14px', marginTop: 12, fontSize: 12, color: statusTokens.info.text, lineHeight: 1.6 }}>
                <strong>What this upload does:</strong><br />
                • <strong>Validation sheet</strong> → updates each site's contractual CPH and KVA
                (<strong>Final DG CPH</strong> / <strong>Final DG</strong>) for the cycle derived from
                that row's own date — used instead of the standard CPH lookup table<br />
                • <strong>Main sheet</strong> → imports per-visit records, same destination as the
                GRATO Daily Report, with each row's own CCPH and Access Ticket<br />
                • Meter readings marked <strong>FAULTY</strong> and CMS RH marked <strong>#N/A</strong> in
                the source file are recorded as missing, not zero
              </div>

              {error && <div style={{ marginTop: 12 }}><ErrorBox message={error} /></div>}

              {importResult && (
                <div style={{
                  marginTop: 14, background: statusTokens.success.bg, border: `1px solid ${statusTokens.success.border}`,
                  borderRadius: 10, padding: '14px 18px',
                }}>
                  <p style={{ fontWeight: 600, color: statusTokens.success.text, margin: '0 0 10px' }}>Import Complete</p>

                  <p style={{ fontSize: 11, fontWeight: 700, color: statusTokens.success.text, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Validation sheet
                  </p>
                  <div style={{ fontSize: 12, color: statusTokens.success.text, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 10 }}>
                    <span>Rows processed:</span><strong>{importResult.validation.rows_total}</strong>
                    <span>Imported:</span><strong>{importResult.validation.rows_imported}</strong>
                    <span>Skipped (dup):</span><strong>{importResult.validation.rows_skipped}</strong>
                    <span>Site budgets updated:</span><strong>{importResult.validation.site_budgets_updated}</strong>
                    <span>Cycle(s) touched:</span><strong>{importResult.validation.cycles_touched?.join(', ') || '—'}</strong>
                  </div>

                  <p style={{ fontSize: 11, fontWeight: 700, color: statusTokens.success.text, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Main sheet
                  </p>
                  <div style={{ fontSize: 12, color: statusTokens.success.text, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <span>Rows processed:</span><strong>{importResult.main.rows_total}</strong>
                    <span>Imported:</span><strong>{importResult.main.rows_imported}</strong>
                    <span>Skipped (dup):</span><strong>{importResult.main.rows_skipped}</strong>
                    <span>Technician links:</span><strong>{importResult.main.technician_links}</strong>
                  </div>

                  <div style={{ marginTop: 10, fontSize: 12, color: importResult.error_count > 0 ? statusTokens.danger.text : statusTokens.success.text }}>
                    Errors: <strong>{importResult.error_count}</strong>
                  </div>

                  {importResult.errors?.length > 0 && (
                    <details style={{ marginTop: 8 }}>
                      <summary style={{ fontSize: 11, cursor: 'pointer' }}>Show errors</summary>
                      {importResult.errors.map((e: string, i: number) => (
                        <p key={i} style={{ fontSize: 11, color: statusTokens.danger.text, margin: '2px 0' }}>{e}</p>
                      ))}
                    </details>
                  )}
                </div>
              )}
            </div>

            {/* ── Upload history ── */}
            <Card style={{ padding: 18, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <SectionLabel style={{ marginBottom: 0 }}>Upload History</SectionLabel>
                <Btn size="sm" variant="ghost" onClick={refetchUploads}>↻ Refresh</Btn>
              </div>
              {uploadsLoading ? <Spinner size={24} /> : uploadList.length === 0 ? (
                <EmptyState icon="📭" title="No uploads yet" desc="Upload the first agreed Validation Template above" />
              ) : (
                <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: neutral[50] }}>
                        {['Filename', 'Status', 'Main Rows', 'Validation Rows', 'Budgets Updated', 'Cycles', 'Uploaded By', 'When'].map(h => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, color: neutral[500], borderBottom: `1px solid ${neutral[200]}`, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadList.map((u: any) => (
                        <tr
                          key={u._id}
                          style={{ borderBottom: `1px solid ${neutral[100]}`, cursor: u.validation_cycles_touched?.length ? 'pointer' : 'default' }}
                          onClick={() => u.validation_cycles_touched?.[0] && setStatsCycle(u.validation_cycles_touched[0])}
                        >
                          <td style={{ padding: '6px 10px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.filename}</td>
                          <td style={{ padding: '6px 10px' }}><StatusPill status={u.status} /></td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>{u.main_rows_imported}/{u.main_rows_total}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>{u.validation_rows_imported}/{u.validation_rows_total}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>{u.site_budgets_updated}</td>
                          <td style={{ padding: '6px 10px', color: neutral[500] }}>{(u.validation_cycles_touched || []).join(', ') || '—'}</td>
                          <td style={{ padding: '6px 10px', color: neutral[500] }}>{u.uploaded_by?.fullName || '—'}</td>
                          <td style={{ padding: '6px 10px', color: neutral[400], fontSize: 11 }}>{fmtDate(u.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* ── Stats for the selected cycle ── */}
          {statsCycle && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: ink }}>Validation Summary — {statsCycle}</h2>
                <Btn size="sm" variant="ghost" onClick={refetchStats}>↻ Refresh</Btn>
              </div>

              {statsLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>}

              {statsData && !statsLoading && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 24 }}>
                    <KpiCard label="Sites Validated" value={statsData.totals?.sites || 0} icon="📍" />
                    <KpiCard label="Total Final RH" value={fmtNum(statsData.totals?.total_final_rh, 0)} icon="⏱️" />
                    <KpiCard label="Total Final Consumption (L)" value={fmtNum(statsData.totals?.total_final_cons)} icon="⛽" />
                    <KpiCard label="Total Management Amount" value={fmtNum(statsData.totals?.total_management_amount, 0)} icon="💰" />
                    <KpiCard label="DG KVA Mismatches" value={statsData.totals?.dg_check_mismatches || 0} icon="⚠️"
                      tone={(statsData.totals?.dg_check_mismatches || 0) > 0 ? 'warn' : undefined} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
                    {/* By cluster */}
                    <Card>
                      <SectionLabel>Final Consumption by Cluster (L)</SectionLabel>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={statsData.by_cluster || []} margin={{ left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={neutral[100]} />
                          <XAxis dataKey="cluster" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={55} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v: any) => fmtNum(v) + ' L'} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="total_final_cons" fill={urgencyTokens.medium.fg} name="Final Cons (L)" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>

                    {/* DG KVA mismatches */}
                    <Card>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                        <SectionLabel style={{ marginBottom: 0 }}>DG KVA Mismatches</SectionLabel>
                        <span style={{ marginLeft: 8, background: statusTokens.danger.bg, color: statusTokens.danger.text, borderRadius: 8, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                          {statsData.dg_check_mismatches?.length || 0}
                        </span>
                      </div>
                      {(!statsData.dg_check_mismatches || statsData.dg_check_mismatches.length === 0) ? (
                        <EmptyState icon="✅" title="No mismatches" desc="SBC-recorded KVA matched the validated value for every site" />
                      ) : (
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: neutral[50] }}>
                                {['Site', 'SBC KVA', 'Final KVA', 'Resolution'].map(h => (
                                  <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, color: neutral[500], borderBottom: `1px solid ${neutral[200]}` }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {statsData.dg_check_mismatches.map((m: any) => (
                                <tr key={m.site_id} style={{ borderBottom: `1px solid ${neutral[100]}` }}>
                                  <td style={{ padding: '6px 10px', fontWeight: 600 }}>{m.site_id}</td>
                                  <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m.sbc_dg_kva}</td>
                                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: urgencyTokens.critical.fg, fontVariantNumeric: 'tabular-nums' }}>{m.final_dg_kva}</td>
                                  <td style={{ padding: '6px 10px', color: neutral[500], fontSize: 11 }}>{m.dg_comment}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Card>
                  </div>

                  {/* CMS vs Field top variances */}
                  <Card>
                    <SectionLabel>Largest CMS vs Field RH Variances</SectionLabel>
                    {(!statsData.cms_vs_field_top || statsData.cms_vs_field_top.length === 0) ? (
                      <EmptyState icon="✅" title="No variances" desc="CMS and field RH agreed for every validated site" />
                    ) : (
                      <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead style={{ position: 'sticky', top: 0, background: neutral[50] }}>
                            <tr>
                              {['Site', 'CMS RH', 'Final RH', 'Variance', 'Comment'].map(h => (
                                <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 11, color: neutral[500], borderBottom: `1px solid ${neutral[200]}`, whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {statsData.cms_vs_field_top.map((r: any) => (
                              <tr key={r.site_id} style={{ borderBottom: `1px solid ${neutral[100]}` }}>
                                <td style={{ padding: '6px 10px', fontWeight: 600, fontSize: 11 }}>{r.site_id}</td>
                                <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.cms_rh != null ? fmtNum(r.cms_rh) : '—'}</td>
                                <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(r.final_rh)}</td>
                                <td style={{
                                  padding: '6px 10px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                  color: Math.abs(r.cms_vs_field) > 50 ? urgencyTokens.critical.fg : neutral[700],
                                }}>{fmtNum(r.cms_vs_field)}</td>
                                <td style={{ padding: '6px 10px', color: neutral[500], fontSize: 11 }}>{r.cms_vs_field_comment || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
